import React, { Component } from "react";
import ToolTip from "@mui/material/Tooltip";
import {
  AutoAwesome,
  AutoFixHigh,
  Close,
  EditNote,
  Explore,
  History as HistoryIcon,
  SmartToy,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Typography,
} from "@mui/material";
import axios from "axios";
import Api from "../../customlib/Api";
import Global from "../../customlib/Global";
import AiHistoryDialog from "./AiHistoryDialog";

// Daftar model AI yang bisa dipilih user sebelum memakai fitur Generate/Polish/Compass
const AI_MODEL_LIST = [
  { value: "gpt-4o-mini", label: "GPT-4o mini" },
  { value: "gpt-4o", label: "GPT-4o" },
  { value: "gemini-1.5-flash", label: "Gemini 1.5 Flash" },
  { value: "claude-3-haiku", label: "Claude 3 Haiku" },
];

const FEATURE_GENERATE = "GENERATE";
const FEATURE_COMPASS = "COMPASS";

// DEV-1405 - pemberitahuan Recap gagal per status respons BE (lainnya: gagal/timeout AI)
const RECAP_FAILED_MESSAGE = "Recap gagal diproses, draft tidak diubah. Silakan coba lagi.";
const RECAP_ERROR_MESSAGES = {
  400: "Belum ada pesan customer di percakapan ini untuk dirangkum.",
  404: "Percakapan tidak ditemukan, silakan muat ulang chat.",
};

class ComposeAi extends Component {
  state = {
    activeFeature: null,
    selectedModel: null,
    aiModels: [],
    isLoadingModel: false,
    isLoadingPolish: false,
    isPolishErrorOpen: false,
    isLoadingRecap: false,
    recapErrorMessage: null,
    suggestionResult: null,
    isLoadingSuggestion: false,
    compassResult: null,
    isLoadingCompass: false,
    isHistoryOpen: false
  };

  handleOpenHistory = () => {
    this.setState({ isHistoryOpen: true });
  };

  handleCloseHistory = () => {
    this.setState({ isHistoryOpen: false });
  };

  handleModelChange = (event) => {
    this.setState({ selectedModel: event.target.value });
  };

  // Klik ulang tombol yang sedang aktif -> tutup panelnya tanpa memanggil AI lagi (MTN-967)
  handleToggleFeature = (feature) => {
    const isOpening = this.state.activeFeature !== feature;

    this.setState({ activeFeature: isOpening ? feature : null }, () => {
      if (!isOpening) return;

      if (feature === FEATURE_GENERATE) {
        this.executeGenerateSuggestion();
      }

      if (feature === FEATURE_COMPASS) {
        this.executeCompass();
      }
    });
  };

  handleUseCompassSuggestion = () => {
    this.props.handleSetDraft(this.state.compassResult?.reply?.suggestedMessage);
    this.props.handleSetSelectedSuggestion({ suggestionLogId: this.state.compassResult?.suggestionLogId, usedSuggestionUuid: this.state.compassResult?.reply.uuid, suggestionValue: this.state.compassResult?.reply.suggestedMessage, sentValue: "" });
    this.handleCloseFeature();
  }

  handleUserGenerateSuggestion = (suggestionLogId, suggestion) => {
    this.props.handleSetDraft(suggestion.text);
    this.props.handleSetSelectedSuggestion({ suggestionLogId: suggestionLogId, usedSuggestionUuid: suggestion.uuid, suggestionValue: suggestion.text, sentValue: "" });
    this.handleCloseFeature();
  }

  handleCloseFeature = () => {
    this.setState({ activeFeature: null });
  };

  ComponentPanelHeader = ({ icon, title, subtitle }) => (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={11} textAlign="left">
        <Typography variant="subtitle1" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
          {icon} {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: "#5a5f73" }}>
            {subtitle}
          </Typography>
        )}
      </Grid>
      <Grid item xs={1} textAlign="center">
        <IconButton color="error" size="small" onClick={this.handleCloseFeature}>
          <Close fontSize="small" />
        </IconButton>
      </Grid>
    </Grid>
  );

  // Placeholder: 4 saran pesan hasil AI
  PanelGenerate = () => (
    <>
      <this.ComponentPanelHeader
        icon={<AutoAwesome fontSize="small" sx={{ color: "#7c4dff" }} />}
        title="Generate Saran Pesan"
        subtitle="AI akan menyarankan 4 alternatif pesan berdasarkan konteks percakapan"
      />
      <Divider sx={{ mt: "8px", mb: "8px" }} />

      {
        this.state.isLoadingSuggestion && <LinearProgress />
      }

      <List sx={{ width: "100%", p: 0 }}>
        {this.state.suggestionResult?.reply?.map((item, index) => (
          <ListItemButton onClick={() => this.handleUserGenerateSuggestion(this.state.suggestionResult?.suggestionLogId, item)} key={item} sx={{ borderRadius: "10px", opacity: 1, mb: "4px" }}>
            <ListItemAvatar>
              <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: "#7c4dff" }}>{index+1}</Avatar>
            </ListItemAvatar>

            {
              this.state.isLoadingSuggestion && (
                <ListItemText
                  primary={<Skeleton variant="text" width="90%" />}
                />
              )
            }

            {
              !this.state.isLoadingSuggestion && (
                <ListItemText
                  primary={item.text}
                />
              )
            }
          </ListItemButton>
        ))}
      </List>

      <Box textAlign="center" sx={{ mt: "6px" }}>
        <Button variant="contained" size="small" startIcon={<AutoAwesome sx={{ fontSize: 14 }} />} onClick={this.executeGenerateSuggestion} sx={{ borderRadius: "16px", textTransform: "none", fontSize: 12, py: "1px", px: "8px", minHeight: 0, lineHeight: 1.4 }}>
          Generate Sekarang
        </Button>
      </Box>
    </>
  );

  // Placeholder: ringkasan kondisi customer dari AI
  PanelCompass = () => (
    <>
      <this.ComponentPanelHeader
        icon={<Explore fontSize="small" sx={{ color: "#2e7d32" }} />}
        title="Compass — Kondisi Customer"
        subtitle="Ringkasan & rekomendasi berdasarkan riwayat percakapan"
      />
      <Divider sx={{ mt: "8px", mb: "8px" }} />

      <Box sx={{ backgroundColor: "#ffffff", borderRadius: "10px", p: "12px" }}>
        
        {
          this.state.isLoadingCompass && (
            <Box>
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="95%" />
              <Skeleton variant="text" width="70%" />
            </Box>
          )
        }

        {
          !this.state.isLoadingCompass && this.state.compassResult && (
            <Box>
              <Typography variant="caption" sx={{ color: "#5a5f73", fontWeight: 600 }}>
                KONDISI CUSTOMER
              </Typography>
              <Typography variant="body2" sx={{ color: "#323233", mb: "10px" }}>
                {this.state.compassResult?.reply?.condition}
              </Typography>

              {this.state.compassResult?.reply?.actions?.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ color: "#5a5f73", fontWeight: 600 }}>
                    SARAN ACTIONS
                  </Typography>
                  <Box sx={{ mt: "4px", mb: "10px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {this.state.compassResult.reply.actions.map((action, index) => (
                      <Chip
                        key={index}
                        size="small"
                        label={action}
                        sx={{
                          backgroundColor: "#e8f5e9",
                          color: "#2e7d32",
                          height: "auto",
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            display: "block",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            py: "4px",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}

              {this.state.compassResult?.reply?.suggestedMessage && (
                <>
                  <Typography variant="caption" sx={{ color: "#5a5f73", fontWeight: 600 }}>
                    SUGGESTED MESSAGE
                  </Typography>
                  <Box sx={{ backgroundColor: "#f4f6f8", borderRadius: "10px", p: "10px", fontSize: 13, color: "#323233" }}>
                    {this.state.compassResult.reply.suggestedMessage}
                  </Box>
                  <Box textAlign="right" sx={{ mt: "6px" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={this.handleUseCompassSuggestion}
                      sx={{ borderRadius: "16px", textTransform: "none", fontSize: 12, py: "1px", px: "8px", minHeight: 0, lineHeight: 1.4 }}
                    >
                      Gunakan Pesan Ini
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )
        }

        {
          !this.state.isLoadingCompass && !this.state.compassResult && (
            <Box sx={{ color: "#8a8f9c", fontSize: 13, textAlign: "center" }}>
              Klik "Analisa Sekarang" untuk melihat kondisi customer, saran actions, dan suggested message.
            </Box>
          )
        }
      </Box>

      <Box textAlign="center" sx={{ mt: "10px" }}>
        <Button
          variant="contained"
          size="small"
          disabled={this.state.isLoadingCompass}
          onClick={this.executeCompass}
          startIcon={<Explore sx={{ fontSize: 14 }} />}
          sx={{ borderRadius: "16px", textTransform: "none", fontSize: 12, py: "1px", px: "8px", minHeight: 0, lineHeight: 1.4 }}
        >
          Analisa Sekarang
        </Button>
      </Box>
    </>
  );

    loadAiModels = () => {
        this.setState({isLoadingModel: true}, () => {
            axios
                .get(Api.getAiModelsList(), Api.getRequestHeaderJsonNew())
                .then((response) => {
            
                    console.log("AI_COMPOSE_MODELS", response.data);
                
                    var responseData = response.data;
                    
                    let models = [];
                    if(responseData.data && responseData.data?.models){
                        responseData.data?.models?.forEach(item => {
                            models.push({value: item, label: item.replaceAll("_", " ")});
                        });
                    }

                    this.setState({aiModels: models, isLoadingModel: false}, () => {
                        if(!this.state.selectedModel && this.state.aiModels?.length > 0){
                            this.setState({selectedModel: models[0].value});
                        }
                    });
            
                })
                .catch((error) => {
                    this.setState({isLoadingModel: false});
                    console.error("There was an error!", error);
                });
        });
    }

    // MTN-967 - id request Polish terakhir; respons dari request lama (mis. sudah pindah chat) diabaikan
    _polishReqId = 0;

    setPolishLoading = (isLoadingPolish) => {
        this.setState({ isLoadingPolish });
        if (this.props.onPolishLoadingChange) {
            this.props.onPolishLoadingChange(isLoadingPolish);
        }
    }

    handlePolishFailed = () => {
        this.setPolishLoading(false);
        this.setState({ isPolishErrorOpen: true });
    }

    handleClosePolishError = () => {
        this.setState({ isPolishErrorOpen: false });
    }

    // MTN-967 - hasil Polish langsung mengganti draft (tidak terkirim), draft sebelumnya disimpan
    // di Compose untuk Undo. Gagal apapun (422/502/timeout/success:false) -> draft tidak disentuh.
    executePolishMessage = () => {
        const draft = this.props.draft;
        if (this.state.isLoadingPolish || !draft || draft.trim().length === 0) return;

        const fromNumber = this.props.currentConversation?.fromNumber;
        const reqId = ++this._polishReqId;
        const isStale = () => reqId !== this._polishReqId || fromNumber !== this.props.currentConversation?.fromNumber;

        this.setPolishLoading(true);

        const params = {
            nomorCustomer: fromNumber,
            message: draft,
            aiModel: this.state.selectedModel
        };

        axios
            .post(Api.aiPolishMessage(), params, Api.getRequestHeaderJsonNew())
            .then((response) => {
                if (isStale()) return;

                const responseData = response.data;
                const reply = responseData?.data?.reply;
                const polished = typeof reply?.polished === "string" ? reply.polished.trim() : "";

                if (!responseData?.success || !polished) {
                    this.handlePolishFailed();
                    return;
                }

                if (this.props.onApplyPolish) {
                    this.props.onApplyPolish(polished, draft);
                } else {
                    this.props.handleSetDraft(polished);
                }
                this.props.handleSetSelectedSuggestion({ suggestionLogId: responseData.data.suggestionLogId, usedSuggestionUuid: reply.uuid, suggestionValue: polished, sentValue: "" });
                this.setPolishLoading(false);
            })
            .catch((error) => {
                if (isStale()) return;
                console.error("There was an error!", error);
                this.handlePolishFailed();
            });
    }

    // DEV-1405 - id request Recap terakhir; respons dari request lama (mis. sudah pindah chat) diabaikan
    _recapReqId = 0;

    setRecapLoading = (isLoadingRecap) => {
        this.setState({ isLoadingRecap });
        if (this.props.onRecapLoadingChange) {
            this.props.onRecapLoadingChange(isLoadingRecap);
        }
    }

    handleRecapFailed = (status) => {
        this.setRecapLoading(false);
        this.setState({ recapErrorMessage: RECAP_ERROR_MESSAGES[status] || RECAP_FAILED_MESSAGE });
    }

    handleCloseRecapError = () => {
        this.setState({ recapErrorMessage: null });
    }

    // DEV-1405 - Recap tidak butuh draft: BE mengambil rangkaian pesan terakhir customer sendiri.
    // Hasil langsung mengganti draft (tidak terkirim), draft sebelumnya (kosong maupun berisi)
    // disimpan di Compose untuk Undo. Gagal apapun -> draft tidak disentuh.
    executeRecap = () => {
        if (this.state.isLoadingRecap || this.state.isLoadingPolish || this.props.hasCustomerMessage === false) return;

        const draft = this.props.draft || "";
        const fromNumber = this.props.currentConversation?.fromNumber;
        const reqId = ++this._recapReqId;
        const isStale = () => reqId !== this._recapReqId || fromNumber !== this.props.currentConversation?.fromNumber;

        this.setRecapLoading(true);

        const params = {
            nomorCustomer: fromNumber,
            aiModel: this.state.selectedModel
        };

        axios
            .post(Api.aiRecap(), params, Api.getRequestHeaderJsonNew())
            .then((response) => {
                if (isStale()) return;

                const responseData = response.data;
                const recap = typeof responseData?.data?.recap === "string" ? responseData.data.recap.trim() : "";

                if (!responseData?.success || !recap) {
                    this.handleRecapFailed();
                    return;
                }

                if (this.props.onApplyRecap) {
                    this.props.onApplyRecap(recap, draft, responseData.data.low_confidence === true);
                } else {
                    this.props.handleSetDraft(recap);
                }
                this.props.handleSetSelectedSuggestion({ suggestionLogId: responseData.data.suggestionLogId, usedSuggestionUuid: responseData.data.reply?.uuid, suggestionValue: recap, sentValue: "" });
                this.setRecapLoading(false);
            })
            .catch((error) => {
                if (isStale()) return;
                console.error("There was an error!", error);
                this.handleRecapFailed(error?.response?.status);
            });
    }

    executeGenerateSuggestion = () => {
        this.setState({isLoadingSuggestion: true}, () => {
            
            const params = {
                nomorCustomer: this.props.currentConversation.fromNumber,
                aiModel: this.state.selectedModel
            };
            
            axios
                .post(Api.aiGenerateSuggestion(), params, Api.getRequestHeaderJsonNew())
                .then((response) => {
            
                    console.log("AI_COMPOSE_MODELS", response.data);
                
                    var responseData = response.data;
                    
                    if(responseData.success){
                        this.setState({suggestionResult: responseData.data, isLoadingSuggestion: false});
                    }
                    
            
                })
                .catch((error) => {
                    this.setState({isLoadingSuggestion: false});
                    console.error("There was an error!", error);
                });
        });
    }

    executeCompass = () => {
        this.setState({isLoadingCompass: true}, () => {
            
            const params = {
                nomorCustomer: this.props.currentConversation.fromNumber,
                aiModel: this.state.selectedModel
            };
            
            axios
                .post(Api.aiCompass(), params, Api.getRequestHeaderJsonNew())
                .then((response) => {
            
                    console.log("AI_COMPOSE_MODELS", response.data);
                
                    var responseData = response.data;
                    
                    if(responseData.success){
                        this.setState({compassResult: responseData.data, isLoadingCompass: false});
                    }
                    
            
                })
                .catch((error) => {
                    this.setState({isLoadingCompass: false});
                    console.error("There was an error!", error);
                });
        });
    }

    // Ganti percakapan (fromNumber berubah) -> kosongkan draft & kembalikan panel AI ke kondisi default
    resetToDefault = () => {
        if (this.props.handleSetDraft) {
            this.props.handleSetDraft("");
        }

        // Batalkan Polish yang sedang berjalan untuk chat sebelumnya
        this._polishReqId++;
        this.setPolishLoading(false);

        // DEV-1405 - Batalkan Recap yang sedang berjalan untuk chat sebelumnya
        this._recapReqId++;
        this.setRecapLoading(false);

        this.setState({
            activeFeature: null,
            isPolishErrorOpen: false,
            recapErrorMessage: null,
            suggestionResult: null,
            isLoadingSuggestion: false,
            compassResult: null,
            isLoadingCompass: false,
        });
    }

    componentDidMount() {
        this.loadAiModels();
    }

    componentDidUpdate(prevProps) {
        const prevFromNumber = prevProps.currentConversation?.fromNumber;
        const currentFromNumber = this.props.currentConversation?.fromNumber;

        if (prevFromNumber !== currentFromNumber) {
            this.resetToDefault();
        }
    }

  render() {
    const { activeFeature, selectedModel } = this.state;

    return (
      <Box sx={{ mb: "6px", backgroundColor: "transparent" }}>
        <Paper
          elevation={0}
          sx={{
            // backgroundColor: "rgba(234, 238, 242, 0.3)",
            backgroundColor: "transparent",
            borderRadius: "8px",
            p: "3px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "6px",
            // ml: "10px",
            // mr: "10px",
            minHeight: 0,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={selectedModel}
              onChange={this.handleModelChange}
              displayEmpty
              input={
                <OutlinedInput
                  startAdornment={
                    <InputAdornment position="start">
                      <SmartToy sx={{ color: "#5a5f73", fontSize: 15 }} />
                    </InputAdornment>
                  }
                />
              }
              sx={{
                backgroundColor: "#f9c391",
                borderRadius: "16px",
                fontSize: 12,
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSelect-select": { py: "3px" },
                textTransform: "capitalize"
              }}
              size="small"
            >
              {this.state.aiModels.map((model) => (
                <MenuItem key={model.value} value={model.value} sx={{ fontSize: 12, textTransform: "capitalize" }}>
                  <span style={{textTransform: "capitalize"}}>{model.label}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <ToolTip title="Dapatkan 4 saran pesan dari AI">
              <Button
                variant={activeFeature === FEATURE_GENERATE ? "contained" : "outlined"}
                size="small"
                startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
                onClick={() => this.handleToggleFeature(FEATURE_GENERATE)}
                sx={{ borderRadius: "16px", textTransform: "none", fontSize: 12, py: "1px", px: "8px", minHeight: 0, lineHeight: 1.4, color: activeFeature === FEATURE_GENERATE ? "#fff" : "#7c4dff", borderColor: "#7c4dff", backgroundColor: activeFeature === FEATURE_GENERATE ? "#7c4dff" : "transparent" }}
              >
                Generate
              </Button>
            </ToolTip>

            <ToolTip title="Perhalus bahasa pesan sebelum dikirim">
              <Button
                variant="outlined"
                size="small"
                startIcon={<AutoFixHigh sx={{ fontSize: 14 }} />}
                onClick={this.executePolishMessage}
                sx={{ borderRadius: "16px", textTransform: "none", fontSize: 12, py: "1px", px: "8px", minHeight: 0, lineHeight: 1.4, color: "#0288d1", borderColor: "#0288d1", backgroundColor: "transparent" }}
                disabled={this.state.isLoadingPolish || this.state.isLoadingRecap || !this.props.draft || this.props.draft.trim().length === 0}
              >
                {this.state.isLoadingPolish ? "Memproses…" : "Polish"}
              </Button>
            </ToolTip>

            {/* DEV-1405 - span pembungkus: Tooltip MUI tidak bisa menangkap event dari button disabled */}
            <ToolTip title={this.props.hasCustomerMessage === false ? "Belum ada pesan customer untuk dirangkum" : "Satu balasan untuk semua pesan terakhir customer"}>
              <span style={{ display: "inline-flex" }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditNote sx={{ fontSize: 14 }} />}
                  onClick={this.executeRecap}
                  sx={{ borderRadius: "16px", textTransform: "none", fontSize: 12, py: "1px", px: "8px", minHeight: 0, lineHeight: 1.4, color: "#ef6c00", borderColor: "#ef6c00", backgroundColor: "transparent" }}
                  disabled={this.state.isLoadingRecap || this.state.isLoadingPolish || this.props.hasCustomerMessage === false}
                >
                  {this.state.isLoadingRecap ? "Memproses…" : "Recap"}
                </Button>
              </span>
            </ToolTip>

            <ToolTip title="Penjelasan kondisi customer dari riwayat percakapan">
              <Button
                variant={activeFeature === FEATURE_COMPASS ? "contained" : "outlined"}
                size="small"
                startIcon={<Explore sx={{ fontSize: 14 }} />}
                onClick={() => this.handleToggleFeature(FEATURE_COMPASS)}
                sx={{ borderRadius: "16px", textTransform: "none", fontSize: 12, py: "1px", px: "8px", minHeight: 0, lineHeight: 1.4, color: activeFeature === FEATURE_COMPASS ? "#fff" : "#2e7d32", borderColor: "#2e7d32", backgroundColor: activeFeature === FEATURE_COMPASS ? "#2e7d32" : "transparent" }}
              >
                Compass
              </Button>
            </ToolTip>

            {/* <ToolTip title="Riwayat AI seluruh sales agent">
              <IconButton
                size="small"
                onClick={this.handleOpenHistory}
                sx={{ backgroundColor: "#ffffff", width: 28, height: 28, ml: "2px" }}
              >
                <HistoryIcon sx={{ fontSize: 16, color: "#5a5f73" }} />
              </IconButton>
            </ToolTip> */}
          </Box>
        </Paper>

        {activeFeature && (
          <Paper
            elevation={2}
            sx={{
              mt: "8px",
              ml: "10px",
              mr: "10px",
              borderRadius: "8px",
              p: "8px 10px",
              backgroundColor: "#deecfd99",
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >
            {activeFeature === FEATURE_GENERATE && <this.PanelGenerate />}
            {activeFeature === FEATURE_COMPASS && <this.PanelCompass />}
          </Paper>
        )}

        <Snackbar
          open={this.state.isPolishErrorOpen}
          autoHideDuration={5000}
          onClose={this.handleClosePolishError}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          message="Polish gagal diproses, draft tidak diubah. Silakan coba lagi."
        />

        <Snackbar
          open={this.state.recapErrorMessage !== null}
          autoHideDuration={5000}
          onClose={this.handleCloseRecapError}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          message={this.state.recapErrorMessage || ""}
        />

        {/* <AiHistoryDialog open={this.state.isHistoryOpen} onClose={this.handleCloseHistory} /> */}
      </Box>
    );
  }
}

export default ComposeAi;
